// MWA-390 — one WebSocket per browser tab, shared by every view that wants live updates.
//
// useWebSocket used to open a socket for every call, so a client record held about
// six of them (the layout, the notification bell, the page and each tab), each
// authenticating and being broadcast to separately. Views now subscribe to this one
// connection instead; the connection stays open while anything is subscribed.
//
// Two things a live page needs that a bare socket does not give it:
//
//  - Catching up. A broadcast that arrives while the socket is down is gone, so after
//    a RE-connect every subscriber is sent the local `ws_reconnected` action and
//    refetches whatever it shows. The first connect sends nothing: a view that has
//    just mounted has just loaded its data.
//  - Noticing a dead socket. A laptop that sleeps does not always tell the page its
//    socket died. Coming back to a tab that was hidden for a while reconnects, and a
//    ping every few minutes keeps API Gateway from dropping an idle connection.

export interface WsMessage {
  action: string
  data: unknown
}
export type WsHandler = (message: WsMessage) => void
export type WsHandlers = Record<string, WsHandler>

/** Sent locally to every subscriber after the socket comes back, never over the wire. */
export const WS_RECONNECTED = 'ws_reconnected'

export interface SocketLike {
  readyState: number
  send(data: string): void
  close(): void
  onopen: ((event?: unknown) => void) | null
  onclose: ((event?: unknown) => void) | null
  onerror: ((event?: unknown) => void) | null
  onmessage: ((event: { data: string }) => void) | null
}

const CONNECTING = 0
const OPEN = 1

interface Timers {
  setTimeout: (fn: () => void, ms: number) => unknown
  clearTimeout: (handle: unknown) => void
  setInterval: (fn: () => void, ms: number) => unknown
  clearInterval: (handle: unknown) => void
}

export interface WsConnectionOptions {
  /** The socket URL including credentials, or null when there is nothing to connect with. */
  getUrl: () => Promise<string | null>
  createSocket: (url: string) => SocketLike
  /** Wait before the Nth reconnect attempt; the last entry repeats. */
  reconnectDelaysMs?: number[]
  keepAliveMs?: number
  /** How long the last subscriber can be gone before the socket is closed. */
  closeGraceMs?: number
  /** A tab hidden at least this long reconnects when it is shown again. */
  staleAfterHiddenMs?: number
  now?: () => number
  timers?: Timers
  logger?: Pick<Console, 'warn' | 'error'>
}

export function createWsConnection(options: WsConnectionOptions) {
  const delays = options.reconnectDelaysMs ?? [2000, 5000, 10000, 30000]
  const keepAliveMs = options.keepAliveMs ?? 4 * 60 * 1000
  const closeGraceMs = options.closeGraceMs ?? 1000
  const staleAfterHiddenMs = options.staleAfterHiddenMs ?? 60 * 1000
  const now = options.now ?? (() => Date.now())
  const timers: Timers = options.timers ?? {
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
    setInterval: (fn, ms) => setInterval(fn, ms),
    clearInterval: (h) => clearInterval(h as ReturnType<typeof setInterval>),
  }
  const logger = options.logger ?? console

  const subscribers = new Set<{ current: WsHandlers }>()
  const stateListeners = new Set<(connected: boolean) => void>()

  let socket: SocketLike | null = null
  let connecting = false
  let connected = false
  let hasConnectedBefore = false
  let attempts = 0
  let generation = 0
  let hiddenAt: number | null = null
  let reconnectTimer: unknown = null
  let keepAliveTimer: unknown = null
  let closeTimer: unknown = null

  function setConnected(next: boolean) {
    if (connected === next) return
    connected = next
    stateListeners.forEach((listener) => listener(next))
  }

  function callHandlers(message: WsMessage) {
    subscribers.forEach((subscriber) => {
      const handler = subscriber.current[message.action]
      if (!handler) return
      try {
        handler(message)
      } catch (err) {
        // One view's bug must not stop the others hearing the same message.
        logger.error(`WebSocket handler for "${message.action}" threw:`, err)
      }
    })
  }

  function receive(raw: string) {
    let message: WsMessage
    try {
      message = JSON.parse(raw)
    } catch (err) {
      logger.error('Failed to parse WebSocket message:', err)
      return
    }
    if (!message || typeof message.action !== 'string') return
    callHandlers(message)
  }

  function stopKeepAlive() {
    if (keepAliveTimer !== null) timers.clearInterval(keepAliveTimer)
    keepAliveTimer = null
  }

  function startKeepAlive() {
    stopKeepAlive()
    // ws-default answers any unknown action with an ack, which no view listens for.
    keepAliveTimer = timers.setInterval(() => send('ping'), keepAliveMs)
  }

  function clearReconnectTimer() {
    if (reconnectTimer !== null) timers.clearTimeout(reconnectTimer)
    reconnectTimer = null
  }

  function scheduleReconnect() {
    if (subscribers.size === 0) return
    clearReconnectTimer()
    const delay = delays[Math.min(attempts, delays.length - 1)]
    attempts += 1
    reconnectTimer = timers.setTimeout(() => {
      reconnectTimer = null
      void connect()
    }, delay)
  }

  async function connect() {
    if (subscribers.size === 0) return
    if (socket && (socket.readyState === OPEN || socket.readyState === CONNECTING)) return
    if (connecting) return

    connecting = true
    const mine = ++generation
    try {
      const url = await options.getUrl()
      // Superseded while the token was being fetched, or everyone left.
      if (mine !== generation || subscribers.size === 0) return
      if (!url) return // signed out or not configured: nothing to retry until someone subscribes again

      const created = options.createSocket(url)
      socket = created

      created.onopen = () => {
        if (socket !== created) return
        setConnected(true)
        attempts = 0
        startKeepAlive()
        if (hasConnectedBefore) callHandlers({ action: WS_RECONNECTED, data: null })
        hasConnectedBefore = true
      }
      created.onclose = () => {
        if (socket !== created) return
        socket = null
        setConnected(false)
        stopKeepAlive()
        scheduleReconnect()
      }
      created.onerror = (event) => {
        logger.error('WebSocket error:', event)
      }
      created.onmessage = (event) => receive(event.data)
    } catch (err) {
      logger.error('Failed to connect WebSocket:', err)
      scheduleReconnect()
    } finally {
      connecting = false
    }
  }

  function send(action: string, data?: Record<string, unknown>) {
    if (socket && socket.readyState === OPEN) {
      socket.send(JSON.stringify({ action, ...(data || {}) }))
    }
  }

  function teardown() {
    closeTimer = null
    if (subscribers.size > 0) return
    clearReconnectTimer()
    stopKeepAlive()
    generation += 1
    const closing = socket
    socket = null
    hasConnectedBefore = false
    attempts = 0
    setConnected(false)
    closing?.close()
  }

  function subscribe(handlers: { current: WsHandlers }) {
    subscribers.add(handlers)
    if (closeTimer !== null) {
      timers.clearTimeout(closeTimer)
      closeTimer = null
    }
    void connect()
    return () => {
      subscribers.delete(handlers)
      if (subscribers.size === 0 && closeTimer === null) {
        // A grace period, not an immediate close: moving between pages unmounts one
        // subscriber and mounts the next in the same tick.
        closeTimer = timers.setTimeout(teardown, closeGraceMs)
      }
    }
  }

  /** Drop a socket that may be dead and open a new one now, which also triggers the catch-up. */
  function forceReconnect() {
    const stale = socket
    socket = null
    stopKeepAlive()
    clearReconnectTimer()
    setConnected(false)
    stale?.close()
    attempts = 0
    void connect()
  }

  function handleVisibility(visible: boolean) {
    if (!visible) {
      hiddenAt = now()
      return
    }
    const hiddenFor = hiddenAt === null ? 0 : now() - hiddenAt
    hiddenAt = null
    if (subscribers.size === 0) return
    if (!socket || socket.readyState !== OPEN) {
      clearReconnectTimer()
      attempts = 0
      void connect()
    } else if (hiddenFor >= staleAfterHiddenMs) {
      forceReconnect()
    }
  }

  function handleOnline() {
    if (subscribers.size === 0) return
    if (!socket || socket.readyState !== OPEN) {
      clearReconnectTimer()
      attempts = 0
      void connect()
    }
  }

  function onConnectionChange(listener: (connected: boolean) => void) {
    stateListeners.add(listener)
    return () => {
      stateListeners.delete(listener)
    }
  }

  return {
    subscribe,
    send,
    isConnected: () => connected,
    onConnectionChange,
    handleVisibility,
    handleOnline,
  }
}
